import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { useField } from "formik";
import "./RichTextEditor.css";

export const stripHtml = (html) => {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").trim();
};

export const sanitizeHtml = (html) => {
  if (!html || typeof html !== "string") return "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const safeTags = new Set(["p", "br", "strong", "b", "em", "i", "u", "s", "h1", "h2", "h3", "h4", "ul", "ol", "li", "blockquote", "a"]);
    const discardTags = new Set(["script", "style", "iframe", "object", "embed", "form", "svg", "math", "template"]);
    const output = document.createElement("div");
    const copySafe = (node, parent) => {
      if (node.nodeType === Node.TEXT_NODE) {
        parent.appendChild(document.createTextNode(node.textContent));
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const tag = node.tagName.toLowerCase();
      if (discardTags.has(tag)) return;
      const target = safeTags.has(tag) ? document.createElement(tag) : parent;
      if (tag === "a") {
        try {
          const url = new URL(node.getAttribute("href"));
          if (["https:", "http:", "mailto:"].includes(url.protocol)) {
            target.setAttribute("href", url.href);
            target.setAttribute("rel", "noopener noreferrer");
          }
        } catch (_) {
          // An invalid URL remains plain text.
        }
      }
      Array.from(node.childNodes).forEach((child) => copySafe(child, target));
      if (target !== parent) parent.appendChild(target);
    };
    Array.from(doc.body.childNodes).forEach((node) => copySafe(node, output));
    return output.innerHTML;
  } catch (e) {
    return stripHtml(html);
  }
};

export const RichTextDisplay = ({ html, fallback = "-" }) => {
  if (!html) return <span>{fallback}</span>;
  const cleanHtml = sanitizeHtml(html);
  return (
    <div
      className="rte-display"
      dangerouslySetInnerHTML={{ __html: cleanHtml }}
    />
  );
};

export const RichTextEditor = ({ value, onChange, onBlur, disabled = false, placeholder = "", minHeight = "200px" }) => {
  return (
    <div className="rte-wrapper" style={{ "--rte-min-height": minHeight }}>
      <CKEditor
        editor={ClassicEditor}
        data={value}
        disabled={disabled}
        config={{ placeholder }}
        onChange={(event, editor) => {
          onChange(editor.getData());
        }}
        onBlur={() => {
          onBlur?.();
        }}
      />
    </div>
  );
};

export const RichTextEditorField = ({ name, disabled = false, placeholder = "", minHeight = "200px" }) => {
  const [field, , helpers] = useField(name);

  return (
    <RichTextEditor
      value={field.value}
      onChange={(html) => helpers.setValue(html)}
      onBlur={() => helpers.setTouched(true)}
      disabled={disabled}
      placeholder={placeholder}
      minHeight={minHeight}
    />
  );
};
